import { differenceInCalendarDays, max, min } from "date-fns"

import type { IBuyDetail, IBuyItem } from "~/hooks/useSupplyPlan"
import { filterDefined } from "~/utils/tsUtils"
import { filterIncomingOrdersInPeriod, filterIncomingOrdersUpTo } from "~/utils/incomingOrdersUtils"

type BuyWindow = { eta: Date; periodEnd: Date }

function toUTCMidnight(d: Date): Date {
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
}

export function buildWindowsBySku(
  items: IBuyItem[],
  purchaseOrders: IBuyDetail["purchaseOrders"],
): Map<string, BuyWindow> {
  const poById = new Map(purchaseOrders.map((p) => [p.id, p]))
  const out = new Map<string, BuyWindow>()
  for (const item of items) {
    if (out.has(item.skuId)) continue
    const po = poById.get(item.purchaseOrderId)
    if (!po) continue
    out.set(item.skuId, {
      eta: toUTCMidnight(po.expectedArrivalDate),
      periodEnd: toUTCMidnight(po.purchasePeriodEndDate),
    })
  }
  return out
}

// How many days of demand this buy was created to cover. Every PO built by `buildNoosPoBatches`
// carries `ETA = buyDate + leadTime` and `periodEnd = coverUntil + leadTime`, so the span is
// lead-time independent and identical across the buy: the gap between the buy date and the date the
// next buy is expected. Reading it back keeps a buy scored against the window it was created for
// instead of the supply plan's current purchase interval, which is what made the buy plan's
// "Suggested order" disagree with the overview it was created from (SLIM-576).
//
// Vendor reassignment and manual edits of a PO's period end can break that uniformity, so take the
// longest span rather than whichever PO happens to come first in an unordered map: the result stays
// the same across refetches, and an outlier can only widen the window (a larger suggestion) instead
// of silently collapsing it towards zero.
export function getBuyCoverageDays(windowsBySku: Map<string, BuyWindow>): number | undefined {
  let longest: number | undefined
  for (const { eta, periodEnd } of windowsBySku.values()) {
    const days = differenceInCalendarDays(periodEnd, eta)
    if (days > 0 && (longest === undefined || days > longest)) longest = days
  }
  return longest
}

// "In period" tooltip text shared by all column headers whose subtitle is "in period".
// Each variant has its own ETA (orderDate + leadTime) and its own periodEnd
// (nextBuyDate + leadTime), so the window length is the span this buy covers. The
// dates differ per variant — the line-level "Covers" column carries the actual span.
export function getInPeriodTooltip(coverageDays: number): string {
  return `In period = each variant's ETA → ETA + this buy's coverage (${coverageDays} days).`
}

export const INCOMING_BUCKETS = ["before", "in", "post"] as const
export type IncomingBucket = (typeof INCOMING_BUCKETS)[number]

export const INCOMING_BUCKET_DESCRIPTIONS: Record<IncomingBucket, string> = {
  before:
    "Open orders arriving on or before this buy's ETA. They are already counted in Projected stock (ETA).",
  in: "Open orders arriving after this buy's ETA, up to and including the end of its coverage period. Orders arriving on or before the ETA are already counted in Projected stock (ETA).",
  post: "Open orders arriving after the end of this buy's coverage period.",
}

export type DayRange = { start?: Date; end?: Date }

const nextDayUTC = (d: Date) => new Date(d.getTime() + 24 * 60 * 60 * 1000)

const sumQuantity = (orders: { quantity: number }[]) =>
  orders.reduce((sum, o) => sum + o.quantity, 0)

// All dates are UTC midnights. Mirrors the stock projection behind Projected stock (ETA): it counts
// an order on its arrival day, lands an overdue order no earlier than tomorrow, and reads an overdue
// buy's stock at today. So "before" is already inside Projected stock (ETA), "in" starts the day
// after, and a boundary that is today or earlier empties the buckets up to it.
export function sumIncomingOrdersByBucket(
  orders: { expectedArrivalDate: Date; quantity: number }[],
  window: BuyWindow,
  today: Date,
): Record<IncomingBucket, number> {
  const tomorrow = nextDayUTC(today)
  const landed = orders.map((o) => ({
    expectedArrivalDate: max([o.expectedArrivalDate, tomorrow]),
    quantity: o.quantity,
  }))
  const before = sumQuantity(filterIncomingOrdersUpTo(landed, window.eta))
  const inPeriod = sumQuantity(filterIncomingOrdersInPeriod(landed, window.eta, window.periodEnd))
  return { before, in: inPeriod, post: sumQuantity(landed) - before - inPeriod }
}

// The arrival days each bucket of `sumIncomingOrdersByBucket` holds, as `[start, end)`: the contract
// of the order-detail query behind the drill-down. An empty bucket gets an empty range.
export function getIncomingBucketRanges(
  window: BuyWindow,
  today: Date,
): Record<IncomingBucket, DayRange> {
  const none = { start: nextDayUTC(today), end: nextDayUTC(today) }
  const beforeEnd = window.eta > today ? window.eta : undefined
  const inEnd = window.periodEnd > today ? window.periodEnd : undefined
  const lastEnd = beforeEnd && inEnd ? max([beforeEnd, inEnd]) : (beforeEnd ?? inEnd)
  return {
    before: beforeEnd ? { end: nextDayUTC(beforeEnd) } : none,
    in: inEnd ? { start: beforeEnd && nextDayUTC(beforeEnd), end: nextDayUTC(inEnd) } : none,
    post: lastEnd ? { start: nextDayUTC(lastEnd) } : {},
  }
}

// The smallest `[start, end)` range covering every given range. A missing bound is unbounded, and an
// empty range covers nothing, so a line with an empty bucket neither narrows nor widens a group's.
export function spanDayRanges(ranges: DayRange[]): DayRange {
  const nonEmpty = ranges.filter((r) => !r.start || !r.end || r.start < r.end)
  if (nonEmpty.length === 0) return ranges[0] ?? {}
  const starts = filterDefined(nonEmpty.map((r) => r.start))
  const ends = filterDefined(nonEmpty.map((r) => r.end))
  return {
    start: starts.length === nonEmpty.length ? min(starts) : undefined,
    end: ends.length === nonEmpty.length ? max(ends) : undefined,
  }
}

export function bucketByDateInWindow(
  items: { date: Date; quantity: number }[],
  start: Date,
  end: Date,
): number {
  let total = 0
  const startMs = start.getTime()
  const endMs = end.getTime()
  for (const item of items) {
    const t = item.date.getTime()
    if (t >= startMs && t < endMs) total += item.quantity
  }
  return total
}
